import { IDropdownOption } from "@fluentui/react";
import { RouterParams } from "App";
import { IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { EGroupCollectIntegrationStatus } from "global/Transform/GroupCollectEmitEventTransformer";
import { EHideReason } from "global/Visibility/EHideReason";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import { ParentVisibilityGraph } from "global/Visibility/ParentVisibilityGraph";
import * as IXrmTypes from "./CRM";
import * as ICustomXrmTypes from "./CRM.type";
import { IFlexibleFormAttributeSetDefinition, IFlexibleFormCategoryDefinition, IFlexibleFormNonStandardDefinition } from "./FlexibleFormModel";
import { ICheckList } from "./ICheckList";

export type TGuid = string;
export type TOptionSet = number;
export type TUnderWritingStatus = "pending" | "rejected" | "approved"

export interface ICESNumber {
    id: string;
    // this is the name
    cesNumber: string;
    deactivated: boolean;
};

export interface IValue {
    type: "entity" |
    "optionset" |
    "string" |
    "number" |
    "float" |
    "date" |
    "bool" |
    "multioptionset";
}

export interface IStringValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "string"
    value: string;
}

export interface IDateValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "date"
    value: string;
}
export interface IBooleanValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "bool"
    value: boolean;
}

export interface ITask {
    taskId: string;
    subject: string;
}

export interface INullableBooleanValue extends IValue {
    type: "bool"
    value: boolean | null
}
export interface INumberValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "number";
    value: number;
}

// Abstract
export interface IReferenceValue extends IValue, IObjectWithNameId {
    type: "entity";
    id: TGuid,
    name: string
}

export interface IEntityReferenceValue extends IReferenceValue {
    type: "entity"
    entityType: 'bcbs_admin' | 'account' | 'bcbs_config_optionsetitem' | 'bcbs_copaycoinoption' | string;
}

export interface IMultiselectOptionSetValueValue {
    id: string;
    name: string;
    value: number;
}

export interface IMultiselectOptionSetValue extends IValue {
    // for now number/float/date are not implemented/needed
    type: "multioptionset"
    entityType: 'bcbs_config_optionsetitem',
    value: string;
    options: IMultiselectOptionSetValueValue[];
}


export interface IOptionSetValue<T = number> extends IValue {
    type: "optionset",
    name: string;
    value: T;
}

export type TValue = IStringValue |
    INumberValue |
    IEntityReferenceValue |
    IBooleanValue |
    IOptionSetValue |
    IDateValue |
    IMultiselectOptionSetValue |
    null;

export type TFieldType = "primary" | "pcp" | "specialist";

// abstract
export interface IFlexibleFieldValue {
    id: TGuid;
    tierRef: IRef | null;

    //TODO change this enum? or drop it
    type: TFieldType;
    value: TValue;

    emitRefId?: string;
}

export interface IObject {
    id: TGuid;
    name: string;
    deactivated: boolean;
}

export interface IFlexibleField {
    id: TGuid;
    name: string;
    //TODO subject to removal soon
    masterAttributeID: TGuid;
    configRef: IRef;
    values: IFlexibleFieldValue[];
    visibility?: EHideReason;
}

// TODO rename this to INonStandardFieldSet
export interface INonStandard {
    id: TGuid;
    name: string;
    sectionRef: IRef | null; // this is to distinguish if null, then its tab level otherwise if populated we need to display at section level
    fields: IFlexibleField[];
    deactivated: boolean;
    referenceOnly?: boolean;
    partOfTemplate?: boolean;
}

export interface INonStandardTemplate extends INonStandard {
    fields: IFlexibleField[];
    linkedCategoryTypeId?: string;
    linkedCategoryName?: string;
    relationships?: ITemplateRelationship[];
}

export interface ITemplateRelationship {
    categoryID: string; //this can be group config, or benefit at the moment
    categoryType: string;
    sectionRefs?: ITemplateSectionRelationship[];
}

export interface ITemplateSectionRelationship {
    sectionId: TGuid;
    nonstandards?: IRef[];
}

export interface ITemplateEntityRelation {
    entityType: string; //can be benefit, fee and premium etc
    id: TGuid;
}

export interface IRef { id: TGuid, name: string }

export interface IBenefitRef extends IRef {
    // TODO depreciated
    bcbs_config_categoryid?: TGuid
};

export interface IPlan {
    id: TGuid;
    name: string;
    feesAndPremiumsRef: IRef | null;
    grandFathered: IOptionSetValue | null;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    nonStandards: INonStandard[];
    benefitRefs: IBenefitRef[];
    fundingType: IOptionSetValue | null;
    billingOnlyPlan: boolean;
    deactivated: boolean;
}

export interface ITierLevel extends IXrmTypes.bcbs_config_tierlevel {
    id: TGuid;
    name: string;
    description: string;
    pcp: boolean;
    ion: boolean;
    isLabelDisabled: boolean;
}

//TODO this extends is very important for later, we can carry on category information into snapshot, very helpful
export enum EBenefitStatus {
    "In-Progress" = 1 as number,
    "Released for Notification" = 2 as number, // this is flipped with a button from new->ready once they are ready
    "Cancelled" = 3 as number,
    "Underwriting Complete" = 4 as number, //  is set at the same time the whole GC is approved.
}

export type TBenefitStatusOptionSetValue = IOptionSetValue<EBenefitStatus>;

export enum EGroupConfigurationStatus {
    // ** NOTE these codes do not match the option set codes on front end
    // they were never intended to match 1 for 1 for status of the record
    "Active" = 1 as number,
    "Cancelled" = 3 as number,
    "Transferred to Other LOB" = 5 as number,

    // seems we can start matching ...
    // matching these should not be a problem as the behavior should be 
    // exactly same and its transient until actual cancellation gets recorded.
    "Cancellation in Progress" = 808900013 as number,
    "Transferred to Other LOB in Progress" = 808900014 as number,
}
export type TGroupConfigurationStatusOptionSetValue = IOptionSetValue<EGroupConfigurationStatus>;


export enum EProduct {
    "No" = 0 as number,
    "Yes" = 1 as number,
}

export type TProductOptionSetValue = IOptionSetValue<EProduct>;

//TODO I think IBenefit should extend from this
// id can be used on update broadcast
// we can store all ICategoryFields in single array lookup?
export interface ICategoryFields {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    tiers: ITierLevel[];
}

export type TDeactivateOptions = IBooleanValue;

export interface IBenefit extends IXrmTypes.bcbs_config_category {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    nonStandards: INonStandard[];
    tiers: ITierLevel[];
    tierSetRef: IRef | null;
    grandFathered: IOptionSetValue | null;
    status: TBenefitStatusOptionSetValue;
    product: TProductOptionSetValue | null;
    fundingType: IOptionSetValue | null;
    deactivated: boolean;
}

export interface IGroupNumber {
    id: TGuid;
    cesNumberRef: IRef | null;
    prefix: string | null;
    base: string | null;
    suffix: string | null;

    // ***primary key
    // auto-calc = {prefix}-{base}-{suffix}
    groupNumber: string | null;
    fields: IFlexibleField[];
    accountsReceivableRef?: IRef | null;
    planRef?: IRef | null;
    stopLossRef?: IRef | null;
    deactivated: boolean;
    status?: TBenefitStatusOptionSetValue;
}

export interface IDepartment {
    id: TGuid;
    name: string | null;
    description: string | null;
    cesNumberRef: IRef | null;
    deactivated: boolean;
}

export interface IAccountsReceivable {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    deactivated: boolean;
}

export interface IConfigAttribute {
    id: TGuid;
    name: string;
    sectionID: TGuid;
    masterAttributeRef: TGuid;
}

export interface INonStandardConfigAttribute extends IConfigAttribute {
    nonStandard: boolean;
    nonStandardAppliesTo: TOptionSet;
    nonStandardCustom: {
        categories: TGuid[],
        sections: TGuid[]
    }
}

export interface IAttributeSetConfigAttribute extends IConfigAttribute {
    attributeSetID: TGuid;
}

export interface IFlexibleFieldSet {
    id: TGuid;
    name: string;
    attributeSetRef: IRef;
    fields: IFlexibleField[];
    deactivated: boolean;
}

export interface IFeesAndPremiums {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    stopLossRef: IRef | null;
    deactivated: boolean;
}

enum ECommentStatus {
    "new" = 1,
    "resolved" = 2
}
export interface IComment {
    id: TGuid;
    // parentCommentId: TGuid;
    comment: string;
    location: RouterParams;
    status: ECommentStatus;
    // newBy?: TGuid;
    // resolvedBy?: TGuid;
}

export interface INotification {
    id: TGuid;
    name: string;
    title?: string;
    description?: string;
    deactivated?: boolean;
    location?: RouterParams;
    regarding?: () => any;
}



export interface IPharmacyORE {
    id: TGuid;
    releaseStatus: "In Progress" | "Ready for Release";
    releaseDate?: IDateValue | null;
    pharmacyPlanDesign: string;
    newPharmacyPlanDesign: string;
    newClientNumber: string;
}

export interface IGroupStructure {
    groupNumbers: IGroupNumber[];
    departments: IDepartment[];
    cesNumbers: ICESNumber[];

}

interface IMap {
    [key: number]: number;
}


export const bcbs_statecode_byStatus: IMap = {

    1: 0, // In Progress -> Active
    2: 1, // Underwriting Review Complete -> InActive
    808900000: 0, // Data Entry Complete -> Active
    808900001: 1, // Retired -> Inactive
    808900002: 1, // Inactive -> Inactive

    // todo is this correct
    808900003: 0, // MNIL Data Entry Complete -> Active ? 
    808900004: 1, // MNIL Underwriting Complete -> Inactive
    808900005: 0, // Corrections Pending -> Active
    808900006: 1, // Gold Standard -> Inactive

    // todo is this correct
    808900009: 0, // Pending Underwriting -> Active
    808900010: 1, // Merged and Closed -> Inactive

    808900012: 1, // "Transferred to Other LOB(808900012)" -> Inactive
    808900011: 1, // Cancelled -> Inactive
};

export interface IObjectWithNameId {
    id: TGuid;
    name: string;
}

export interface IGroupConfigurationData extends IObjectWithNameId {
    // used for schema compatibility check generated by group library app 
    // 0 - 0 day migration
    // 1 - Group Collection Integration
    // 
    iteration: number;
    id: TGuid;
    name: string;
    bcbs_shortname: string;
    effectiveDate: IDateValue | null;
    operativeDate: IDateValue | null;
    changeType: IOptionSetValue | null;
    fundingType: IOptionSetValue | null;
    dualFunded: IBooleanValue | null;
    erisa: IOptionSetValue | null;
    grandFathered: IOptionSetValue | null;
    MNIL: IBooleanValue | null;
    lineOfBusiness: IOptionSetValue | null;
    fields: IFlexibleField[];
    nonStandards: INonStandard[];
    nonStandardTemplates: INonStandardTemplate[];
    attributeSets: IFlexibleFieldSet[];
    plans: IPlan[];
    benefits: IBenefit[];
    comments: IComment[];
    groupStructure: IGroupStructure;
    claimsARs: IAccountsReceivable[];
    feesAndPremiums: IFeesAndPremiums[];
    stopLoss: IStopLoss[];
    showInActives: IBooleanValue | null;
    reconciliation: IBooleanValue | null;
    pharmacyORE?: IPharmacyORE[];
    status: TGroupConfigurationStatusOptionSetValue;
    releaseRef?: IRef;
    releaseVersion?: string;
    opportunity?: IEntityReferenceValue | null;
    groupCollectStatus?: EGroupCollectIntegrationStatus;
    isRevision: IBooleanValue | null;
    revisionTasks: IEntityReferenceValue[];
    notifications?: INotification[];

    // latest app version that touched the file
    appVersion: string;


    // Not used
    deactivated?: boolean;

    // use to flag which transformers have ran
    // make sure to update the c#/backend/model class and deploy app services

    transformers?: {
        Sprint17?: true,
        Sprint18?: true,
        Sprint21?: true,
        Sprint22?: true,
        Sprint24?: true,
        Sprint30?: true,
        Sprint33?: true,
        Sprint33PatchTransformer?: true,
        Sprint34?: true,
    },

    audit?: {
        modifiedBy?: IEntityReferenceValue;
    }
}

export interface IStopLoss {
    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    attributeSets: IFlexibleFieldSet[];
    deactivated: boolean;
}

export interface ISprintInfoItem {
    date: number;
    systemVersion: number;
    productionVersion: number;
}

export interface ISprintInfo {
    sprint17: ISprintInfoItem;
    sprint18: ISprintInfoItem;
    sprint21: ISprintInfoItem;
    sprint22: ISprintInfoItem;
    sprint24: ISprintInfoItem;
    sprint30: ISprintInfoItem;
    sprint33: ISprintInfoItem;
    sprint33Patch: ISprintInfoItem;
    // Sprint34: ISprintInfoItem;
}


export interface IGroupConfigurationConfig {
    bcbs_bccadmin: IXrmTypes.bcbs_bccadmin[];
    rows: IXrmTypes.bcbs_config_row[];
    segments: IXrmTypes.bcbs_config_segment[]
    categories: IXrmTypes.bcbs_config_category[],
    tabs: IXrmTypes.bcbs_config_tab[],
    sections: IXrmTypes.bcbs_config_section[],
    attributes: IXrmTypes.bcbs_config_attribute[],
    attributeNonStandardSections: IXrmTypes.bcbs_config_attribute_bcbs_config_secti[],
    masterattributes: IXrmTypes.bcbs_config_masterattribute[],
    optionsets: IXrmTypes.bcbs_config_optionset[],
    optionsetitems: IXrmTypes.bcbs_config_optionsetitem[]

    tiersets: IXrmTypes.bcbs_config_tierset[],
    tierlevels: IXrmTypes.bcbs_config_tierlevel[],
    copaycoinoptions: ICustomXrmTypes.bcbs_config_categoryNavCopay[],
    globalOptionSetDefinitions: ICustomXrmTypes.IOptionSetDefinition[],

    // note: nothing from this should be saved or loaded
    // only runtime
    appOptionSets: {
        bcbs_fundingtypes: IOptionSetValue[] | undefined;
        bcbs_groupconfigurationchangetypecode: IOptionSetValue[] | undefined;
        bcbs_erisacode: IOptionSetValue[] | undefined;
        bcbs_lob: IOptionSetValue[] | undefined;
        bcbs_grandfatheredcode: IOptionSetValue[] | undefined;
        bcbs_idcardorderingoptioncode: IOptionSetValue[] | undefined;
    },

    // note: nothing from this should be saved or loaded
    // only used during runtime
    cache: {

        // bcbs_config_tab.bcbs_config_tabid > bcbs_config_tab
        map_bcbs_config_tab: Record<string, IXrmTypes.bcbs_config_tab>;

        map_bcbs_config_section: Record<string, IXrmTypes.bcbs_config_section>;
        map_bcbs_config_attribute: Record<string, IXrmTypes.bcbs_config_attribute>;
        map_bcbs_config_attribute_by_masterAttributeId: Record<string, IXrmTypes.bcbs_config_attribute[]>;
        map_bcbs_config_masterattribute: Record<string, IXrmTypes.bcbs_config_masterattribute>;
        map_bcbs_config_category: Record<string, IXrmTypes.bcbs_config_category>;
        map_bcbs_config_category_named: Record<ICustomXrmTypes.TCategoryName, IXrmTypes.bcbs_config_category>;
        map_bcbs_copaycoinoption_type: Record<string, ICustomXrmTypes.ICopayCoin[]>
        map_bcbs_config_attribute_sets: Record<string, IXrmTypes.bcbs_config_attribute[]>
        map_bcbs_bccadmin: Record<string, IXrmTypes.bcbs_bccadmin>;

        map_bcbs_config_masterattribute_cancellations: Record<string, IXrmTypes.bcbs_config_attribute>;
        // _bcbs_optionsetid_value > bcbs_config_optionsetitem[]
        map_bcbs_config_optionsetitems_bySetId: Record<string, IXrmTypes.bcbs_config_optionsetitem[]>;

        map_bcbs_config_optionsetitems: Record<string, IXrmTypes.bcbs_config_optionsetitem>;

        map_bcbs_config_tierlevels_by_tierSetId: Record<string, IXrmTypes.bcbs_config_tierlevel[]>;

        bcbs_config_category_benefits: IXrmTypes.bcbs_config_category[];


        options_bcbs_lob: IDropdownOption<IOptionSetValue>[]
        options_bcbs_fundingtypes: IDropdownOption<IOptionSetValue>[]
        options_bcbs_erisacode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_grandfatheredcode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_changetypecode: IDropdownOption<IOptionSetValue>[]
        options_bcbs_idcardorderingoptioncode: IDropdownOption<IOptionSetValue>[]
        options_benefitStatusReasons: IDropdownOption<TBenefitStatusOptionSetValue>[],
        options_groupConfigurationStatusReasons: IDropdownOption<TGroupConfigurationStatusOptionSetValue>[],
        options_status: IDropdownOption<IBooleanValue>[];
        options_products: IDropdownOption<TProductOptionSetValue>[],

        // UI / Field generation helpers
        categoryFormDefinitions: Record<string, IFlexibleFormCategoryDefinition>;
        attributeSetFormDefinitions: Record<string, IFlexibleFormAttributeSetDefinition>;
        nonStandardFormDefinitions: Record<string, IFlexibleFormNonStandardDefinition>;

        parentVisibilityGraph: ParentVisibilityGraph;

    }

    bcbs_config_release: IXrmTypes.bcbs_config_release;

    sprintInfo: ISprintInfo;

}

export type TScope = IGroupConfigurationData | IPlan | IBenefit | IFeesAndPremiums;

export type TCheckList = Record<string, ICheckList>;

export interface ITierDefinition {
    tierSet: IXrmTypes.bcbs_config_tierset | null;
    tierLevels: ITierLevel[];
};

export interface IOpportunityProductOptionBenefit extends IXrmTypes.bcbs_config_category {

    id: TGuid;
    name: string;
    fields: IFlexibleField[];
    visibility: IVisibilityScope;

    bcbs_tierdefinition: ITierDefinition;

    deactivated: boolean;
    won?: boolean;
    requiresUpdate?: boolean;
    select?: boolean;

    bcbs_opportunitybenefits?: IXrmTypes.bcbs_opportunitybenefit[];
    bcbs_opportunityoption?: IXrmTypes.bcbs_opportunityoption;

    // ENROLLMENT
    bcbs_buyupuwsinglei?: number;
    bcbs_buyupuwfamilyi?: number;
    bcbs_buyupuweechildreni?: number;
    bcbs_buyupuweespousei?: number;
    bcbs_buyupuwtotali?: number;

    // RATES
    bcbs_buyupuwsinglem?: number;
    bcbs_buyupuwfamilym?: number;
    bcbs_buyupuweechildrenm?: number;
    bcbs_buyupuweespousem?: number;
    bcbs_buyupuwtotalm?: number;
    bcbs_annualpremium?: number;

    validationMessages: IValidationMessage[];
    bcbs_opportunityproduct?: IXrmTypes.bcbs_opportunityproduct;

    instructions: {
        onSaveOptionSkip?: boolean;
        onSaveOptionDeactivate?: boolean;
        onSaveOptionNoDelta?: boolean;
        onSaveOptionBenefitsSkip?: boolean;
    }

}

export interface IOpportunityProduct {
    opportunity: IXrmTypes.opportunity,
    options: IOpportunityProductOptionBenefit[];
    bcbs_opportunityproduct: IXrmTypes.bcbs_opportunityproduct;
    bcbs_quoterequest: IXrmTypes.bcbs_quoterequest,
}